<template>
  <div>
    <div class="title">
      <Breadcrumb>
        <BreadcrumbItem :to='{ path: "/monitor/resourceGroup"}'>{{$t('BSH.resourceGroup')}}</BreadcrumbItem>
        <BreadcrumbItem>{{this.$route.query.id ? this.$t('alarms.common.edit') : this.$t('alarms.common.add')}}</BreadcrumbItem>
      </Breadcrumb>
    </div>
    <Form ref="resourceGroupForm" :label-width="110" :model="formData" :rules="ruleValidate">
      <Row :gutter="10" style="margin-bottom: 10px;">
        <Col span="24">
          <Card>
            <p slot="title">{{ $t("BSH.basicInfo") }}</p>
            <Row v-if="company == 1">
              <Col span="10">
                <FormItem prop="companyId" :label="$t('BSH.companyName')">
                  <Select v-if="!isEdit" v-model="formData.companyId" filterable :placeholder="$t('alarms.common.pleaseSelectCompany')" :transfer="true" @on-change="getAccountList">
                    <Option :value="item.id" v-for="item in companyList" :key="item.id">{{item.name}}</Option>
                  </Select>
                  <div v-else>{{editData.companyName}}</div>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span="10">
                <FormItem prop="resourceGroupName" :label="$t('BSH.resourceGroupName')">
                  <Input v-model="formData.resourceGroupName" :placeholder="$t('BSH.searchResourceGroupName')" />
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span="10">
                <FormItem prop="accountId" :label="$t('BSH.account')">
                  <Select v-if="!isEdit" v-model="formData.accountId" filterable :placeholder="$t('RAM.common.PleaseSelectAccount')" :transfer="true" @on-change="changeAccount">
                    <Option :value="item.id" v-for="item in accountList" :key="item.id">{{item.name}}</Option>
                  </Select>
                  <div v-else>{{editData.accountName}}</div>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span="10">
                <FormItem prop="regions" :label="$t('BSH.region')">
                  <Select v-if="!isEdit" v-model="formData.regions" filterable multiple :placeholder="$t('alarms.common.pleaseSelectRegions')" :transfer="true" :max-tag-count="1" @on-change="changeRegion">
                    <Checkbox v-model="singleRegion" @on-change="selectRegionAll" style="display: block;text-align:right;">{{ $t('configuration_management.customized_alarm_config.checkAll') }}</Checkbox>
                    <Option :value="item.key" v-for="item in regionList" :key="item.id">{{item.name}}</Option>
                  </Select>
                  <div v-else>{{editData.regionName}}</div>
                </FormItem>
              </Col>
            </Row>
          </Card>
          <Card style="margin-top: 20px">
            <p slot="title">{{ $t("BSH.resourceAllocation") }}</p>
            <Row>
              <Col span="10">
                <FormItem prop="tagKey" :label="$t('BSH.tagKey')">
                  <Select v-if="!isEdit" v-model="formData.tagKey" filterable :placeholder="$t('BSH.searchTagKey')" :transfer="true" @on-change="getTagValueList">
                    <Option :value="item" v-for="(item, index) in tagKeyList" :key="index">{{item}}</Option>
                  </Select>
                  <div v-else>{{editData.tagKey}}</div>
                </FormItem>
              </Col>
              <Col span="10">
                <FormItem prop="tagValue" :label="$t('BSH.tagValue')">
                  <Select v-if="!isEdit" v-model="formData.tagValue" filterable :placeholder="$t('BSH.searchTagValue')" :transfer="true" @on-change="getResourceList">
                    <Option :value="item" v-for="(item, index) in tagValueList" :key="index">{{item}}</Option>
                  </Select>
                  <div v-else>{{editData.tagValue}}</div>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span="10">
                <FormItem :label="$t('alarms.common.resourceType')">
                  <Select v-model="formData.resourceTypeName" filterable :placeholder="$t('resourceLifecycle.filterByResourceType')" :transfer="true" @on-change="changeResourceType">
                    <Option :value="item.name" v-for="item in resourceTypeList" :key="item.id">{{item.name}}</Option>
                  </Select>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col>
                <!-- 动态表头 -->
                <Checkbox-group
                  v-model="tableColumnsChecked"
                  @on-change="changeTableColumns"
                >
                  <Collapse>
                    <Panel name="1"><span style="font-size:13px;font-weight:600;">{{
                        $t("alarms.common.dynamicHeader")
                      }}</span>
                      <template slot="content">
                        <Row>
                          <Col span="24">
                            <Row>
                              <Col span="6">
                                <Checkbox label="resourceIdentifier">{{ $t("BSH.uniqueIdentifierOfResource") }}</Checkbox>
                              </Col>
                              <Col span="6">
                                <Checkbox label="resourceName">Tag: Name</Checkbox>
                              </Col>
                              <Col span="6">
                                <Checkbox label="resourceTypeName">{{ $t('alarms.common.resourceType') }}</Checkbox>
                              </Col>
                              <Col span="6">
                                <Checkbox label="regionName">{{$t("BSH.region")}}</Checkbox>
                              </Col>
                              <Col span="6">
                                <Checkbox label="monitoringState" disabled>{{$t("BSH.monitoringState")}}</Checkbox>
                              </Col>
                              <Col span="6">
                                <Checkbox label="stateName" disabled>{{ $t('smartMonitor.applicationSystemAlert.index') }}</Checkbox>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </template>
                    </Panel>
                  </Collapse>
                </Checkbox-group>
                <Table
                  :no-data-text="$t('common.noData')"
                  :loading="showLoading"
                  :columns="columns"
                  :data="formData.resourceTypeName != '' && formData.resourceTypeName != $t('ApprovalTask.selectAll') ? filterData : tableData"
                  style="width: 100%;"
                  border
                >
                  <template slot-scope="{ row, index }" slot="monitoringState">
                    <i-switch v-model="row.monitoringState" @on-change="changeMonitoringState(index)" />
                  </template>
                  <template slot-scope="{ row, index }" slot="stateName">
                    <Button type="primary" size="small" @click="chooseItem(row.id, row.resourceType, index)">{{$t('resourceLifecycle.view')}}</Button>
                  </template>
                </Table>
              </Col>
            </Row>
          </Card>
          <Card style="margin-top: 20px">
            <p slot="title">{{ $t("BSH.alarmConfiguration") }}</p>
            <Row>
              <Col span="10">
                <FormItem :label="$t('alarms.common.contactType')">
                  <RadioGroup v-model="disabledGroup" @on-change="changeContactType(disabledGroup)">
                    <Radio :label="0">{{$t('alarm_contact.contact')}}</Radio>
                    <Radio :label="1">{{$t('alarm_contact.contactGroup')}}</Radio>
                  </RadioGroup>
                </FormItem>
              </Col>
            </Row>
            <Row v-if="isChangeContanctType">
              <Col span="10">
                <FormItem prop="notificationObjectId" :label="$t('alarms.common.notificationObject')">
                  <Select v-model="formData.notificationObjectId" filterable multiple :placeholder="$t('BSH.pleaseSelectNotificationObject')" :max-tag-count="1" :transfer="true">
                    <Option :value="item.id" v-for="(item, index) in notificationObjectList" :key="index">{{item.name}}</Option>
                  </Select>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span="10">
                <FormItem prop="alarmWayId" :label="$t('alarms.common.alarmType')">
                  <Select v-model="formData.alarmWayId" filterable multiple :placeholder="$t('BSH.pleaseSelectAlarmType')" :max-tag-count="1" :transfer="true">
                    <Option :value="item.id" v-for="item in alarmWayList" :key="item.id">{{item.name}}</Option>
                  </Select>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col span="10">
                <FormItem prop="delayTypeId" :label="$t('alarms.common.alarmDelay')">
                  <Select v-model="formData.delayTypeId" filterable :placeholder="$t('BSH.pleaseSelectAlarmDelay')" :transfer="true">
                    <Option :value="item.id" v-for="item in delayTypeList" :key="item.id">{{item.name}}</Option>
                  </Select>
                </FormItem>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Form>
    <div slot="footer" style="float: right;">
      <Button @click="cancel">{{ $t("alarms.common.back") }}</Button>
      <Button type="primary" @click="ok" :loading="loading">{{ $t("resourceLifecycle.submit") }}</Button>
    </div>
    <Modal :title="$t('smartMonitor.applicationSystemAlert.index')" v-model="showDTOList" width="800" footer-hide :styles="{top:'65px'}">
      <Table
        :no-data-text="$t('common.noData')"
        :loading="DTOLoading"
        :columns="column"
        :data="companyMonitoritemVariablesDTOList"
        border
        :height="500"
        style="width: 100%;"
      >
        <template slot-scope="{ row, index }" slot="customerMonitorIndexDefaultValue">
          <InputNumber :min="0" :max="row.unitName === '%' ? 100 : 9999" v-model="row.customerMonitorIndexDefaultValue" size="small" @on-change="changeNumber(row.customerMonitorIndexDefaultValue, row.id, index)"></InputNumber>
        </template>
        <template slot-scope="{ row, index }" slot="state">
          <i-switch v-model="row.state" @on-change="changeState(row.id, index)" />
        </template>
      </Table>
    </Modal>
  </div>
</template>

<script>
import {getCookie} from "../../../utils/cookieUtil";

export default {
  inject: ['reload'],
  name: "AddOrUpdateResourceGroup",
  data() {
    // 资源组名称是否重复
    let validResourceGroupNameCheck = (rule, value, callback) => {
      this.$api.resourcePermis.duplicateCheckResourceGroup({
        companyId: this.formData.companyId,
        resourceGroupName: this.formData.resourceGroupName
      }).then(res => {
          if (res.status == 200) {
            callback();
          }
        })
        .catch(err => {
          if (err.status == 500) {
            callback(new Error(this.$t("alarms.resourceGroup.msg.resourceGroupNameDuplicate")));
          }
        });
    };
    return {
      company: getCookie(global.companyId),
      companyList: [],
      loading: false,
      singleRegion: false,
      formData: {
        companyId: "",
        resourceGroupName: "",
        accountId: "",
        regions: [],
        tagKey: "",
        tagValue: "",
        notificationObjectId: [],
        alarmWayId: [],
        delayTypeId: "",
        resourceTypeName: ""
      },
      accountList: [],
      regionList: [],
      tagKeyList: [],
      tagValueList: [],
      contactList: [],
      contactGroupList: [],
      notificationObjectList: [],
      alarmWayList: [],
      delayTypeList: [],
      resourceTypeList: [],
      tableColumnsChecked: [
        "resourceIdentifier",
        "resourceName",
        "resourceTypeName",
        "regionName",
        "monitoringState",
        "stateName",
      ],
      tableData: [],
      filterData: [],
      companyMonitoritemVariablesDTOList: [],
      columns: [
        {
          title: this.$t("BSH.uniqueIdentifierOfResource"),
          key: "resourceIdentifier"
        },
        {
          title: "Tag: Name",
          key: "resourceName"
        },
        {
          title: this.$t('alarms.billing.resourceType'),
          key: "resourceTypeName"
        },
        {
          title: this.$t("BSH.region"),
          key: "regionName"
        },
        {
          title: this.$t("BSH.monitoringState"),
          key: "monitoringState",
          slot: "monitoringState",
          fixed: "right"
        },
        {
          title: this.$t('smartMonitor.dashboard.index'),
          key: "stateName",
          slot: "stateName",
          fixed: "right"
        }
      ],
      ruleValidate: {
        companyId: [{ required: true, message: this.$t("alarms.resourceGroup.msg.companyRequired"), trigger: 'change', type: "number"}],
        resourceGroupName: [
          {required: true, message: this.$t("alarms.resourceGroup.msg.resourceGroupNameRequired"), trigger: 'blur'},
          {validator: validResourceGroupNameCheck, trigger: 'blur'}
        ],
        accountId: [{required: true, message: this.$t("alarms.resourceGroup.msg.accountRequired"), trigger: 'change', type: "number"}],
        regions: [{required: true, message: this.$t("alarms.resourceGroup.msg.regionsRequired"), trigger: 'change', type: "array"}],
        tagKey: [{required: true, message: this.$t("alarms.resourceGroup.msg.tagKeyRequired"), trigger: 'change', type: "string"}],
        tagValue: [{required: true, message: this.$t("alarms.resourceGroup.msg.tagValueRequired"), trigger: 'change', type: "string"}],
        notificationObjectId: [{required: true, message: this.$t("alarms.resourceGroup.msg.notificationObjectRequired"), trigger: 'change', type: "array"}],
        alarmWayId: [{required: true, message: this.$t("alarms.resourceGroup.msg.alarmWayRequired"), trigger: 'change', type: "array"}],
        delayTypeId: [{required: true, message: this.$t("alarms.resourceGroup.msg.delayTypeRequired"), trigger: 'change', type: "number"}],
      },
      column: [
        {
          title: this.$t("BSH.monitorIndexName"),
          key: "monitorIndexName"
        },
        {
          title: this.$t('alarms.common.cycle'),
          key: "cycleName"
        },
        {
          title: this.$t('alarms.common.continuousCycle'),
          key: "continousCycleName"
        },
        {
          title: this.$t('alarms.common.statisticsValue'),
          key: "statisticsCountName"
        },
        {
          title: this.$t('alarms.common.sign'),
          key: "relationshipSignName"
        },
        {
          title: this.$t('alarms.common.threshold'),
          slot: "customerMonitorIndexDefaultValue",
          width: "120"
        },
        {
          title: this.$t('alarms.common.unit'),
          key: "unitName"
        },
        {
          title: this.$t("BSH.monitoringState"),
          slot: "state"
        },
      ],
      disabledGroup: 0,
      chooseId: null,
      isEdit: false,
      editData: {},
      resourceGroupId: "",
      showLoading: false,
      showDTOList: false,
      DTOLoading: false,
      isChangeContanctType: true
    }
  },
  mounted() {
    this.init(this.$route.query.id);
  },
  methods: {
    // 初始化
    init(id) {
      this.$refs['resourceGroupForm'].resetFields();
      this.$api.company
        .getCompanyDropDownBox("userName", getCookie(global.cookieName))
        .then((res) => {
          this.companyList = res.data;
          this.formData.companyId = this.companyList[0].id;
          this.getAccountList();
          this.alarmWay();
          this.getAlarmDelay();
          this.getResourceTypeList();
        })
      if (this.$route.query.id == undefined) {
        this.isEdit = false;
        this.tableData = [];
        this.formData.resourceGroupName = "";
        this.formData.notificationObjectId = [];
        this.formData.alarmWayId = [];
        this.formData.delayTypeId = "";
        this.formData.accountId = "";
        this.disabledGroup = 0;
        this.contactList = [];
        this.contactGroupList = [];
        this.notificationObjectList = [];
      } else {
        this.isEdit = true;
        this.showLoading = true;
        this.$api.resourcePermis.getResourceGroupInfo(id).then(res => {
          this.editData = {
            companyName: res.data.companyName,
            accountName: res.data.accountName,
            regionName: res.data.regionName,
            tagKey: res.data.tagKey,
            tagValue: res.data.tagValue
          };
          this.formData.companyId = res.data.companyId;
          this.formData.resourceGroupName = res.data.resourceGroupName;
          this.formData.notificationObjectId = res.data.contactType === 0 ? res.data.contactIDs : res.data.contactGroupIds;
          this.formData.alarmWayId = res.data.noticeType;
          this.formData.delayTypeId = res.data.delay;
          this.formData.accountId = res.data.accountId;
          this.disabledGroup = res.data.contactType;
          this.tableData = res.data.resourceMetricRelationDTOS;
          this.resourceGroupId = res.data.resourceGroupId;
          this.$api.resourcePermis.queryContactList({accountId: this.formData.accountId}).then(res1 => {
            this.contactList = res1.data.contactList;
            if (this.disabledGroup === 0) {
              this.notificationObjectList = this.contactList;
            }
          })
          this.$api.resourcePermis.queryContactGroupList(this.formData.accountId).then(res1 => {
            this.contactGroupList = res1.data;
            if (this.disabledGroup === 1) {
              this.notificationObjectList = this.contactGroupList;
            }
          })
        }).finally(() => {
          this.showLoading = false;
        })
      }
    },
    // 切换区域
    changeRegion() {
      if (
        this.formData.regions.length === this.regionList.length &&
        this.formData.regions.length > 0
      ) {
        this.singleRegion = true;
      } else {
        this.singleRegion = false;
      }
      if (this.formData.regions.length <= 0) {
        return;
      }
      let data = {
        accountId: this.formData.accountId,
        regions: this.formData.regions
      }
      this.$api.resourcePermis.queryTagKey(data).then(res => {
        this.tagKeyList = res.data;
        this.formData.tagKey = "";
        this.formData.tagValue = "";
        this.tagValueList = [];
      })
    },
    // 获取tagValue列表
    getTagValueList() {
      if (!this.formData.tagKey) {
        return;
      }
      let data = {
        accountId: this.formData.accountId,
        regions: this.formData.regions,
        tagKey: this.formData.tagKey
      }
      this.$api.resourcePermis.queryTagValue(data).then(res => {
        this.tagValueList = res.data;
        this.formData.tagValue = "";
      })
    },
    // 获取资源列表
    getResourceList() {
      if (!this.formData.tagValue) {
        return;
      }
      this.showLoading = true;
      this.tableData = [];
      let data = {
        accountId: this.formData.accountId,
        regions: this.formData.regions,
        tagKey: this.formData.tagKey,
        tagValue: this.formData.tagValue
      }
      this.$api.resourcePermis.queryResourceAndDefaultMetric(data).then(res => {
        this.tableData = res.data;
      }).finally(() => {
        this.showLoading = false;
      })
    },
    // 获取资源类型列表
    getResourceTypeList() {
      this.$api.resourcePermis.queryResourceTypeList().then(res => {
        this.resourceTypeList = res.data;
        this.resourceTypeList[0].name = this.$t('ApprovalTask.selectAll');
      })
    },
    // 全选区域
    selectRegionAll() {
      this.formData.regions = [];
      if (this.singleRegion) {
        this.regionList.map((item) => {
          this.formData.regions.push(item.key);
        });
      } else {
        this.formData.regions = [];
      }
    },
    // 获取账户列表
    getAccountList() {
      this.$api.account
        .getAWSAccountByCompanyId("companyId", this.formData.companyId)
        .then(res => {
          this.accountList = res.data;
        });
    },
    // 切换账户
    changeAccount() {
      if (!this.formData.accountId) {
        return;
      }
      this.$api.account
        .queryAccountRegions("accountId", this.formData.accountId)
        .then(res => {
          this.regionList = res.data;
          this.formData.regions = [];
          this.formData.tagKey = "";
          this.tagKeyList = [];
          this.formData.tagValue = "";
          this.tagValueList = [];
        });
      this.$api.resourcePermis.queryContactList({accountId: this.formData.accountId}).then(res => {
        this.disabledGroup = 0;
        this.contactList = res.data.contactList;
        this.changeContactType(0);
      })
      this.$api.resourcePermis.queryContactGroupList(this.formData.accountId).then(res => {
        this.contactGroupList = res.data;
      })
    },
    // 切换联系方式
    changeContactType(i) {
      this.isChangeContanctType = false;
      this.formData.notificationObjectId = [];
      this.notificationObjectList = i === 0 ? this.contactList : this.contactGroupList;
      setTimeout(() => {
        this.isChangeContanctType = true;
      }, 0)
    },
    // 查询报警方式
    alarmWay() {
      this.$api.alertRule
        .alarmWay("userName", getCookie(global.cookieName))
        .then(res => {
          this.alarmWayList = res.data;
        });
    },
    // 查询报警延迟
    getAlarmDelay() {
      this.$api.alertRule.alarmDelay("userName", getCookie(global.cookieName))
        .then(res => {
          this.delayTypeList = res.data;
        })
    },
    // 筛选资源类型
    changeResourceType() {
      this.filterData = this.tableData.filter(item => {
        return item.resourceTypeName === this.formData.resourceTypeName;
      })
    },
    // 查看指标
    chooseItem(id, type, index) {
      this.chooseId = id;
      this.showDTOList = true;
      if (this.tableData[index].companyMonitoritemVariablesDTOList) {
        this.companyMonitoritemVariablesDTOList = this.tableData[index].companyMonitoritemVariablesDTOList;
        return;
      }
      let data = {
        companyId: this.formData.companyId
      }
      if (this.isEdit) {
        data.resourceGroupId = this.tableData[index].resourceGroupId;
        data.resourceRelationId = id;
      } else {
        data.resourceType = type;
      }
      this.DTOLoading = true;
      this.$api.resourcePermis.queryMonitoritemVariablesByResourceType(data).then(res => {
        this.companyMonitoritemVariablesDTOList = res.data;
        for (let i = 0; i < this.companyMonitoritemVariablesDTOList.length; i++) {
          this.companyMonitoritemVariablesDTOList[i].customerMonitorIndexDefaultValue = parseInt(this.companyMonitoritemVariablesDTOList[i].customerMonitorIndexDefaultValue);
        }
        this.tableData[index].companyMonitoritemVariablesDTOList = this.companyMonitoritemVariablesDTOList;
      }).finally(() => {
        this.DTOLoading = false;
      })
    },
    changeNumber(e, id, index) {
      for (let i = 0; i < this.tableData.length; i++) {
        if (this.tableData[i].id === this.chooseId && this.tableData[i].companyMonitoritemVariablesDTOList[index].id === id) {
          this.tableData[i].companyMonitoritemVariablesDTOList[index].customerMonitorIndexDefaultValue = e;
        }
      }
    },
    // 指标开关
    changeMonitoringState(i) {
      this.tableData[i].monitoringState = !this.tableData[i].monitoringState;
    },
    changeState(id, index) {
      for (let i = 0; i < this.tableData.length; i++) {
        if (this.tableData[i].id === this.chooseId && this.tableData[i].companyMonitoritemVariablesDTOList[index].id === id) {
          this.tableData[i].companyMonitoritemVariablesDTOList[index].state = !this.tableData[i].companyMonitoritemVariablesDTOList[index].state;
        }
      }
    },
    // 取消
    cancel() {
      this.$router.push({path: '/monitor/resourceGroup'});
    },
    // 提交
    ok() {
      if (this.formData.resourceGroupName === ""
        || this.formData.delayTypeId === "" || this.formData.delayTypeId === undefined
        || this.formData.alarmWayId === "" || this.formData.alarmWayId === undefined
        || this.formData.notificationObjectId === "" || this.formData.notificationObjectId === undefined) {
        this.$Message.error(this.$t('alarms.resourceGroup.msg.fillAllRequired'));
        return;
      }
      if (!this.isEdit) {
        if (!this.formData.accountId
          || !this.formData.regions
          || !this.formData.tagKey
          || !this.formData.tagValue) {
          this.$Message.error(this.$t('alarms.resourceGroup.msg.fillAllRequired'));
          return;
        }
      }
      this.loading = true;
      let data = {
        companyId: this.formData.companyId,
        resourceGroupName: this.formData.resourceGroupName,
        contactType: this.disabledGroup,
        delay: this.formData.delayTypeId,
        noticeType: this.formData.alarmWayId,
        resourceMetricRelationDTOS: this.tableData
      }
      for (let i = 0; i < data.resourceMetricRelationDTOS.length; i++) {
        if (data.resourceMetricRelationDTOS[i].companyMonitoritemVariablesDTOList) {
          for (let j = 0; j < data.resourceMetricRelationDTOS[i].companyMonitoritemVariablesDTOList.length; j++) {
            data.resourceMetricRelationDTOS[i].companyMonitoritemVariablesDTOList[j].customerMonitorIndexDefaultValue = data.resourceMetricRelationDTOS[i].companyMonitoritemVariablesDTOList[j].customerMonitorIndexDefaultValue.toString();
          }
        }
      }
      if (this.disabledGroup === 0) {
        data.contactIDs = this.formData.notificationObjectId;
      } else {
        data.contactGroupIds = this.formData.notificationObjectId;
      }
      if (this.isEdit) {
        data.resourceGroupId = this.resourceGroupId;
        this.$api.resourcePermis.editResourceGroup(data).then(res => {
          this.$Message.success(this.$t('resourceLifecycle.response.editSuccess'));
          this.$router.push({path: '/monitor/resourceGroup'});
        }).finally(() => {
          this.loading = false;
        })
      } else {
        data.accountId = this.formData.accountId;
        data.regions = this.formData.regions;
        data.tagKey = this.formData.tagKey;
        data.tagValue = this.formData.tagValue;
        this.$api.resourcePermis.addResourceGroup(data).then(res => {
          this.$Message.success(this.$t('resourceLifecycle.response.addSuccess'));
          this.$router.push({path: '/monitor/resourceGroup'});
        }).finally(() => {
          this.loading = false;
        })
      }
    },
    //动态表头
    getColumns() {
      const tableColumnList = {
        resourceIdentifier: {
          title: this.$t("BSH.uniqueIdentifierOfResource"),
          key: "resourceIdentifier"
        },
        resourceName: {
          title: "Tag: Name",
          key: "resourceName"
        },
        resourceTypeName: {
          title: this.$t('alarms.billing.resourceType'),
          key: "resourceTypeName"
        },
        regionName: {
          title: this.$t("BSH.region"),
          key: "regionName"
        },
        monitoringState: {
          title: this.$t("BSH.monitoringState"),
          key: "monitoringState",
          slot: "monitoringState",
          fixed: "right"
        },
        stateName: {
          title: this.$t('smartMonitor.dashboard.index'),
          key: "stateName",
          slot: "stateName",
          fixed: "right"
        },
      };
      let data = [];
      this.tableColumnsChecked.forEach(item => data.push(tableColumnList[item]));
      return data;
    },
    // 切换动态表头
    changeTableColumns() {
      this.columns = this.getColumns();
    }
  }
}
</script>

<style scoped>
.title {
  width: 100%;
  height: 48px;
  background-color: #fff;
  border-bottom: 2px solid #eaeceb;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  padding: 14px;
  margin-bottom: 20px;
}
.ivu-breadcrumb {
  font-size: 13px;
  font-weight: 600;
  color: #515a6e;
}
</style>



// WEBPACK FOOTER //
// src/components/monitor/ResourceGroup/AddOrUpdateResourceGroup.vue